import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { FC, useState } from "react";
import { FadeHorizontalScroll } from "@/components/horizontal-scroll/FadeHorizontalScroll";
import { MdsIconKind } from "@/design-system/components/icon";
import { LensKind } from "@/modules/lenses/types";
import { DateFacetFilter } from "@/components/filters/DateFacetFilter/DateFacetFilter";
import { ContactsFacetFilter } from "@/components/filters/ContactsFacetFilter";
import { CollectionsFacetFilter } from "@/components/filters/CollectionsFacetFilter/CollectionsFacetFilter";
import { NotesListFilter } from "@/modules/filters/notes/NotesListFilter";
import { MediaKindsFacetFilter } from "@/components/filters/MediaKindsFacetFilter/MediaKindsFacetFilter";
import { PageBannerSection } from "@/components/banners/page-banner-section";
import { useTabPageStore } from "@/hooks/useTabPageStore";
import {
  MDSIconButton,
  MDSText,
  MDSTextVariant,
  MDSTooltip,
  MDSTooltipPlacement,
  MDSLensGroup,
  MDSPanelHeader,
  MDSPanelHeaderSlotName,
  MDSIconButtonSize,
  MDSPanelHeaderButtonGroup,
} from "@/mds";
import { SortDropdownButton } from "@/components/filters/SortDropdownButton";

export const NotesListPageHeader: FC = observer(() => {
  const page = useTabPageStore("notesListPage");
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const tabs = getTabs(page.filters);

  return (
    <Wrapper>
      <MDSPanelHeader>
        <MDSText
          slot={MDSPanelHeaderSlotName.Heading}
          variant={MDSTextVariant.HeaderXl}
          numberOfLines={1}
        >
          Notes
        </MDSText>
        <MDSLensGroup slot={MDSPanelHeaderSlotName.Subsection} items={tabs} />
        <MDSPanelHeaderButtonGroup slot={MDSPanelHeaderSlotName.Actions}>
          <MDSTooltip
            config={{
              label: "Show filters",
              placement: MDSTooltipPlacement.Top,
            }}
          >
            <MDSIconButton
              size={MDSIconButtonSize.Medium}
              data-test-id="show-filters-button"
              iconKind={MdsIconKind.Filter}
              onClick={() => setShowFilters(!showFilters)}
              isToggled={showFilters}
            />
          </MDSTooltip>
        </MDSPanelHeaderButtonGroup>
      </MDSPanelHeader>
      {showFilters && (
        <WrapperBottom>
          <FiltersContainer>
            <SortDropdownButton
              isToggled={page.filters.sortBy !== NotesListFilter.DEFAULT_SORT_BY}
              contentList={page.filters.sortOptions}
              label={page.filters.sortLabel}
              placement="below-right-alignment"
              dropdownIcon
            />
            {renderSortedFilters(page.filters)}
          </FiltersContainer>
        </WrapperBottom>
      )}
      <PageBannerSection />
    </Wrapper>
  );
});

NotesListPageHeader.displayName = "NotesListPageHeader";

const renderSortedFilters = (pageFilters: NotesListFilter) => {
  const filters = [
    {
      isActive:
        pageFilters.selectedCollectionTitles.length > 0 ||
        pageFilters.collectionNoCollectionIsSelected,
      defaultOrder: 0,
      component: (
        <CollectionsFacetFilter
          key="collections"
          supportNoCollectionOption
          searchText={pageFilters.collectionsSearchQuery}
          items={pageFilters.collectionsFilterItems}
          onSearchTextChange={pageFilters.setCollectionSearchQuery}
          onSearchTextClear={pageFilters.clearCollectionSearchQuery}
          onSelectItem={pageFilters.toggleCollectionItem}
          onSelectClear={pageFilters.clearCollections}
          buttonPrefix={pageFilters.collectionNoCollectionIsSelected ? "" : "In"}
          buttonLabel="Collections"
          selectedModeKey={pageFilters.selectedCollectionModeKey}
          onSelectMode={pageFilters.onCollectionModeSelectionChange}
          noCollectionIsSelected={pageFilters.collectionNoCollectionIsSelected}
          onNoCollectionIsSelected={pageFilters.setCollectionNoCollectionIsSelected}
          selectedItems={pageFilters.selectedCollectionTitles}
        />
      ),
    },
    {
      isActive: !!pageFilters.mediaKindsFacetFilterItems.some(item => item.isSelected),
      defaultOrder: 1,
      component: (
        <MediaKindsFacetFilter
          key="media-kinds"
          items={pageFilters.mediaKindsFacetFilterItems}
          onSelectItem={pageFilters.toggleMediaKindItem}
          onSelectClear={pageFilters.clearMediaKinds}
          selectedItems={pageFilters.selectedMediaKinds}
        />
      ),
    },
    {
      isActive: !!pageFilters.params.filter?.date,
      defaultOrder: 2,
      component: (
        <DateFacetFilter
          key="date"
          items={pageFilters.dateFilterItems}
          selectedRange={pageFilters.dateSelectedRange}
          onClear={() => pageFilters.clearDateFilter()}
        />
      ),
    },

    {
      isActive: pageFilters.modifiedBySearchItems.some(item => item.isSelected),
      defaultOrder: 3,
      component: (
        <ContactsFacetFilter
          key="modifiedBy"
          items={pageFilters.modifiedBySearchItems}
          searchText={pageFilters.modifiedBySearchQuery}
          onSearchTextChange={pageFilters.setModifiedBySearchQuery}
          onSearchTextClear={pageFilters.clearModifiedBySearchQuery}
          onSelectItem={pageFilters.toggleModifiedByItem}
          onSelectClear={pageFilters.clearModifiedBy}
          buttonPrefix="Edited by"
        />
      ),
    },
    {
      isActive: pageFilters.createdBySearchItems.some(item => item.isSelected),
      defaultOrder: 4,
      component: (
        <ContactsFacetFilter
          key="createdBy"
          items={pageFilters.createdBySearchItems}
          searchText={pageFilters.createdBySearchQuery}
          onSearchTextChange={pageFilters.setCreatedBySearchQuery}
          onSearchTextClear={pageFilters.clearCreatedBySearchQuery}
          onSelectItem={pageFilters.toggleCreatedByItem}
          onSelectClear={pageFilters.clearCreatedBy}
          buttonPrefix="Created by"
        />
      ),
    },
  ];

  return filters
    .sort((a, b) => {
      const activeSort = Number(b.isActive) - Number(a.isActive);
      if (activeSort !== 0) {
        return activeSort;
      }

      return a.defaultOrder - b.defaultOrder;
    })
    .map(filter => filter.component);
};

const Wrapper = styled.div(() => ({}));

const WrapperBottom = styled.div(({ theme }) => ({
  padding: theme.spacing.md,
}));

const FiltersContainer = styled(FadeHorizontalScroll)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: theme.spacing.sm,
}));

const getTabs = (pageFilters: NotesListFilter) => {
  return [
    {
      label: "All",
      isActive: pageFilters.params.lens === LensKind.All,
      onClick: () => pageFilters.setLens(LensKind.All),
    },
    {
      label: "Created by me",
      isActive: pageFilters.params.lens === LensKind.AddedByMe,
      onClick: () => pageFilters.setLens(LensKind.AddedByMe),
    },
    {
      label: "Shared with me",
      isActive: pageFilters.params.lens === LensKind.SharedWithMe,
      onClick: () => pageFilters.setLens(LensKind.SharedWithMe),
    },
  ];
};
